/**
 * Buffers id→route observations seen while inspector mode is on and flushes
 * them to the usage index in batches. Only ever called from the inspector-on
 * path, so production users never reach this code. Deduped per session so a
 * route that renders the same ID repeatedly posts it at most once.
 */
import type { UsageEntry } from '../core/types';

const FLUSH_DELAY_MS = 1500;
/** Shared with StaticInspectorMount.tsx's inline script (non-hydrating routes). */
export const USAGE_ENDPOINT = '/api/i18n-review/usage';
const ENDPOINT = USAGE_ENDPOINT;

const sessionSeen = new Set<string>();
let pending: Array<{ id: string } & UsageEntry> = [];
let timer: ReturnType<typeof setTimeout> | null = null;

async function flush(): Promise<void> {
  timer = null;
  if (pending.length === 0) return;
  const batch = pending;
  pending = [];
  try {
    await fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entries: batch }),
    });
  } catch {
    // Best-effort: usage recording must never disrupt the page. Re-seed the
    // dedupe set so a later flush can retry these ids.
    for (const e of batch) sessionSeen.delete(`${e.id} ${e.route}`);
  }
}

export function recordUsage(id: string, route: string, component?: string): void {
  if (!id) return;
  const key = `${id} ${route}`;
  if (sessionSeen.has(key)) return;
  sessionSeen.add(key);
  pending.push({ id, route, component });
  if (timer == null) timer = setTimeout(flush, FLUSH_DELAY_MS);
}
